import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card } from "@/hq/HQPrimitives";
import { toast } from "sonner";

/**
 * HQ Settings → Prices.
 * Reads /api/hq/control/pricing/plans (base catalog + overrides merged)
 * and PUTs partial updates back. Overrides live in
 * `core_pricing_overrides`; the public OrangeCare catalog reads the
 * same merge so changes ship instantly.
 */

const CURRENCIES = ["GBP", "AED", "USD", "EUR"];
const MODES = [
  { value: "poa",           label: "POA" },
  { value: "fixed",         label: "Fixed" },
  { value: "starting_from", label: "Starting from" },
];

const CUR_SYM = { GBP: "£", AED: "AED ", USD: "$", EUR: "€" };
const fmtMinor = (cur, m) => {
  if (m == null || !Number.isFinite(Number(m))) return "—";
  const major = Number(m) / 100;
  return `${CUR_SYM[cur] || cur + " "}${cur === "AED" ? Math.round(major) : major.toFixed(2)}`;
};
const parseMajor = (cur, s) => {
  if (s === "" || s == null) return null;
  const n = Number(String(s).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

function PlanCard({ plan, onSave }) {
  const [mode, setMode] = useState(plan.pricing_mode || "fixed");
  const [prices, setPrices] = useState(() => {
    const out = {};
    for (const c of CURRENCIES) {
      const p = (plan.prices || {})[c] || {};
      out[c] = { monthly: fmtMinorInput(c, p.monthly), annual: fmtMinorInput(c, p.annual) };
    }
    return out;
  });
  const [busy, setBusy] = useState(false);

  const disabled = mode === "poa";

  const save = async () => {
    setBusy(true);
    try {
      const payload = { pricing_mode: mode, prices: {} };
      if (mode !== "poa") {
        for (const c of CURRENCIES) {
          const m = parseMajor(c, prices[c].monthly);
          const a = parseMajor(c, prices[c].annual);
          const band = {};
          if (m != null) band.monthly = m;
          if (a != null) band.annual = a;
          if (Object.keys(band).length) payload.prices[c] = band;
        }
      }
      const r = await hqApi.put(`/control/pricing/plans/${plan.plan_key}`, payload);
      onSave?.(r.data?.plan);
      toast.success(`${plan.name} updated`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid={`hq-prices-card-${plan.plan_key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">
            {plan.plan_key}
          </div>
          <h3 className="mt-1 font-black text-lg">{plan.name}</h3>
        </div>
        <div className="shrink-0">
          <label className="text-[10px] tracking-[0.20em] uppercase font-bold text-[#8A8A8A] block mb-1">Mode</label>
          <select
            data-testid={`hq-prices-mode-${plan.plan_key}`}
            value={mode} onChange={(e) => setMode(e.target.value)}
            className="px-3 py-2 rounded-lg border border-[#EAEAEA] bg-white text-sm"
          >
            {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        {CURRENCIES.map(cur => (
          <div key={cur} data-testid={`hq-prices-${plan.plan_key}-${cur}`}
               className={`p-3 rounded-lg border ${disabled ? "border-dashed border-[#EAEAEA] opacity-50" : "border-[#EAEAEA]"}`}>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#0A0A0A]">{cur}</div>
            <label className="text-[10px] text-[#8A8A8A] block mt-2">Monthly {CUR_SYM[cur]}</label>
            <input
              data-testid={`hq-prices-${plan.plan_key}-${cur}-monthly`}
              value={prices[cur].monthly}
              disabled={disabled}
              onChange={(e) => setPrices(p => ({ ...p, [cur]: { ...p[cur], monthly: e.target.value } }))}
              className="w-full mt-0.5 px-2 py-1 rounded border border-[#EAEAEA] text-sm disabled:bg-[#F6F5F2]"
              placeholder="0.00"
            />
            <label className="text-[10px] text-[#8A8A8A] block mt-2">Annual {CUR_SYM[cur]}</label>
            <input
              data-testid={`hq-prices-${plan.plan_key}-${cur}-annual`}
              value={prices[cur].annual}
              disabled={disabled}
              onChange={(e) => setPrices(p => ({ ...p, [cur]: { ...p[cur], annual: e.target.value } }))}
              className="w-full mt-0.5 px-2 py-1 rounded border border-[#EAEAEA] text-sm disabled:bg-[#F6F5F2]"
              placeholder="0.00"
            />
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-[#8A8A8A]">
          {disabled
            ? "POA — no numeric prices publicly shown. The card displays “POA” in every currency."
            : mode === "starting_from"
              ? "Prices render with a “from” prefix on the public site."
              : "Fixed prices — public site shows the exact values."}
        </div>
        <button
          data-testid={`hq-prices-save-${plan.plan_key}`}
          onClick={save} disabled={busy}
          className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00] disabled:opacity-50"
        >{busy ? "Saving…" : "Save"}</button>
      </div>
    </Card>
  );
}

function fmtMinorInput(cur, minor) {
  if (minor == null || !Number.isFinite(Number(minor))) return "";
  const major = Number(minor) / 100;
  return cur === "AED" ? String(Math.round(major)) : major.toFixed(2);
}

export default function HQPricesPanel() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState("loading");

  const load = () => {
    setStatus("loading");
    hqApi.get("/control/pricing/plans")
      .then(r => { setPlans(r.data?.plans || []); setStatus("ready"); })
      .catch(() => setStatus("error"));
  };
  useEffect(load, []);

  return (
    <div className="space-y-4" data-testid="hq-prices-panel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Pricing</div>
          <h2 className="mt-0.5 font-black text-2xl">Public prices</h2>
          <p className="mt-1 text-sm text-[#4A4A4A]">
            Overrides ship instantly — no deploy required. Currencies are independent (no FX).
          </p>
        </div>
        <button onClick={load}
          className="text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A] hover:text-black">
          Reload
        </button>
      </div>
      {status === "loading" && <div className="text-sm text-[#8A8A8A]">Loading canonical prices…</div>}
      {status === "error"   && <div className="text-sm text-[#C1350B]">Unable to load prices.</div>}
      {status === "ready" && (
        <div className="space-y-4">
          {plans.map(p => (
            <PlanCard key={p.plan_key} plan={p} onSave={load}/>
          ))}
        </div>
      )}
    </div>
  );
}
